/**
 * The one heartbeat the shell subscribes to.
 *
 * The react-three-fiber canvas drives `tick(dt)` once per rendered frame
 * (before the render); everything the old main.ts frame() did outside the 3D
 * layer - the 20 Hz sim accumulator, the 0.15 s scene pushes, the HUD
 * targeting - lives in subscribers registered here.
 *
 * THE RULE, and it is load-bearing: frame-path subscribers write DOM through
 * refs and mutate stores - they NEVER call React setState. A subscriber that
 * renders React at frame rate recreates the 60 Hz-render bug this design
 * exists to prevent. React learns about frame-derived facts on the slow tick
 * at most, and only through the observer stores.
 */

type Tick = (dt: number) => void

const frameSubs = new Set<Tick>()
const slowSubs = new Set<Tick>()

/** The 0.15 s clock the scene pushes always ran on (main.ts's visual throttle). */
const SLOW_INTERVAL = 0.15
let slowAccumulator = 0

export const frameBus = {
  /** Every rendered frame, before the render. Returns unsubscribe. */
  onFrame(fn: Tick): () => void {
    frameSubs.add(fn)
    return () => {
      frameSubs.delete(fn)
    }
  },

  /**
   * A few times a second. `dt` is the real elapsed time since the last slow
   * tick - what a place-saver or an accumulator actually wants.
   */
  onSlow(fn: Tick): () => void {
    slowSubs.add(fn)
    return () => {
      slowSubs.delete(fn)
    }
  },

  tick(dt: number): void {
    for (const fn of [...frameSubs]) fn(dt)
    slowAccumulator += dt
    if (slowAccumulator > SLOW_INTERVAL) {
      const elapsed = slowAccumulator
      slowAccumulator = 0
      for (const fn of [...slowSubs]) fn(elapsed)
    }
  },
}
